import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Search, BadgeCheck, MapPin, Store, X, Loader2, ShieldCheck, Star, Package, Truck, Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { huntGearCategories } from "@/lib/gearMap";

const TYPES = [{ k: "", label: "All" }, { k: "hunt", label: "Guided Hunts" }, { k: "product", label: "Products" }, { k: "service", label: "Services" }];
const GEAR_CATS = ["Packs", "Boots", "Clothing", "Optics", "Archery", "Firearms", "Calls & Decoys", "Camping", "Accessories", "Other"];

export function effStock(l) {
  if (l.variants?.length) {
    if (l.variants.every((v) => v.stock == null)) return null;
    return l.variants.reduce((s, v) => s + (v.stock ?? 0), 0);
  }
  return l.inventory;
}
const VERIF = { verified_business: "Verified Business", verified_outfitter: "Verified Outfitter", verified_guide: "Verified Guide", verified_retailer: "Verified Retailer" };
const CAT_LABEL = { guide_quality: "Guide", communication: "Comms", lodging: "Lodging", hunt_organization: "Organization", listing_accuracy: "Accuracy", value: "Value", overall_experience: "Experience", product_quality: "Quality", durability: "Durability", description_accuracy: "As described", shipping_service: "Shipping", quality: "Quality" };

function Stars({ value, onChange, size = 16 }) {
  return (
    <span className="inline-flex gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={onChange ? () => onChange(n) : undefined} className={onChange ? "cursor-pointer" : "cursor-default"} aria-label={`${n} star`} data-testid={onChange ? `star-${n}` : undefined}>
          <Star size={size} className={n <= value ? "fill-blaze text-blaze" : "text-saddle/40"} />
        </button>
      ))}
    </span>
  );
}

function ReviewsBlock({ listing, business }) {
  const [reviews, setReviews] = useState([]);
  const [elig, setElig] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [cats, setCats] = useState({});
  const [catDefs, setCatDefs] = useState({});
  const [busy, setBusy] = useState(false);
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();
  const bizId = business?.id;

  const loadReviews = useCallback(() => { if (bizId) api.getReviews({ business_id: bizId }).then(setReviews).catch(() => {}); }, [bizId]);
  useEffect(() => { loadReviews(); api.reviewCategories().then(setCatDefs).catch(() => {}); }, [loadReviews]);
  useEffect(() => { if (isAuthed && bizId) api.reviewEligibility(bizId).then(setElig).catch(() => {}); }, [isAuthed, bizId]);

  const defs = catDefs[listing?.listing_type] || [];
  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  const submit = async () => {
    if (!isAuthed) return openAuth("register");
    setBusy(true);
    try {
      await api.createReview({ business_id: bizId, listing_id: listing.id, rating, categories: cats, comment: comment.trim() || null });
      toast.success("Thanks for your review!");
      setComment(""); setCats({}); loadReviews();
      api.reviewEligibility(bizId).then(setElig);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit review"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 border-t border-saddle/15" data-testid="reviews-block">
      <div className="flex items-center justify-between mb-3">
        <h4 className="hr-overline text-saddle">Reviews {avg && `· ${avg}★ (${reviews.length})`}</h4>
      </div>

      {elig?.eligible && (
        <div className="bg-bone/60 p-4 mb-4" data-testid="review-form">
          <p className="text-sm font-semibold text-forest mb-2">You hunted/bought here — leave a review</p>
          <div className="flex items-center gap-2 mb-2"><span className="text-xs text-charcoal/60">Overall</span><Stars value={rating} onChange={setRating} /></div>
          {defs.length > 0 && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-2">
              {defs.filter((c) => c !== "overall_experience").map((c) => (
                <div key={c} className="flex items-center justify-between"><span className="text-[11px] text-charcoal/60">{CAT_LABEL[c] || c}</span><Stars value={cats[c] || 0} onChange={(n) => setCats({ ...cats, [c]: n })} size={13} /></div>
              ))}
            </div>
          )}
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share the details of your experience…" rows={2} className="w-full border border-saddle/25 px-3 py-2 text-sm outline-none" data-testid="review-comment" />
          <button onClick={submit} disabled={busy} className="mt-2 bg-forest text-bone px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid="submit-review">{busy ? <Loader2 size={14} className="animate-spin inline" /> : "Post review"}</button>
        </div>
      )}
      {elig && !elig.eligible && elig.already_reviewed && <p className="text-xs text-charcoal/50 mb-3">You&apos;ve already reviewed this business — thank you!</p>}

      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.id} className="bg-white border border-saddle/15 p-3" data-testid="review-item">
            <div className="flex items-center justify-between"><strong className="text-forest text-sm">{r.author_name}</strong><Stars value={r.rating} /></div>
            {r.comment && <p className="text-sm text-charcoal/75 mt-1">{r.comment}</p>}
            {Object.keys(r.categories || {}).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">{Object.entries(r.categories).map(([k, v]) => <span key={k} className="text-[10px] bg-bone px-1.5 py-0.5 text-charcoal/60">{CAT_LABEL[k] || k}: {v}★</span>)}</div>
            )}
            {r.merchant_reply && <div className="mt-2 pl-3 border-l-2 border-forest/30"><span className="text-[11px] font-semibold text-forest">{business?.name} replied:</span><p className="text-xs text-charcoal/70">{r.merchant_reply}</p></div>}
          </div>
        ))}
        {reviews.length === 0 && <p className="text-sm text-charcoal/50" data-testid="no-reviews">No reviews yet — be the first after your hunt.</p>}
      </div>
    </div>
  );
}

function ListingModal({ id, onClose, saved, onToggleSave }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gi, setGi] = useState(0);
  const [variantId, setVariantId] = useState(null);
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();

  useEffect(() => { if (id) { setGi(0); setVariantId(null); api.listingDetail(id).then(setData).catch(() => {}); } else setData(null); }, [id]);

  const l = data?.listing;
  const variants = l?.variants || [];
  const selVariant = variants.find((v) => v.id === variantId) || null;
  const effPrice = selVariant && selVariant.price != null ? selVariant.price : (l?.price || 0);
  const selStock = selVariant ? selVariant.stock : (l ? effStock(l) : null);

  const buy = async () => {
    if (!isAuthed) return openAuth("register");
    if (variants.length && !variantId) return toast.error("Please choose an option");
    setBusy(true);
    try {
      const res = await api.marketplaceCheckout({ listing_id: id, origin_url: window.location.origin, variant_id: variantId || undefined });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusy(false); }
  };

  if (!id) return null;
  const b = data?.business;
  const soldOut = variants.length ? (selVariant && selVariant.stock != null && selVariant.stock <= 0) : (selStock != null && selStock <= 0);
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] bg-forest/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid="listing-modal">
        <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} className="bg-paper w-full sm:max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {!l ? <div className="p-10 text-center text-charcoal/50">Loading…</div> : (
            <div>
              <div className="p-6 border-b border-saddle/15">
                <div className="flex items-start justify-between">
                  <span className="text-[10px] font-bold uppercase bg-saddle/15 text-saddle px-2 py-0.5">{l.listing_type}</span>
                  <div className="flex items-center gap-3">
                    {l.listing_type === "product" && <button onClick={() => onToggleSave?.(l.id)} aria-label="Save for later" data-testid="modal-save-btn"><Heart size={20} className={saved ? "fill-blaze text-blaze" : "text-charcoal/40 hover:text-blaze"} /></button>}
                    <button onClick={onClose} aria-label="Close" data-testid="listing-modal-close"><X size={22} className="text-charcoal/50 hover:text-forest" /></button>
                  </div>
                </div>
                <h2 className="font-serif text-3xl text-forest mt-2">{l.title}</h2>
                <div className="flex items-center gap-2 mt-2 text-sm text-charcoal/60">
                  <Store size={14} /> {b?.name}
                  {b?.verification_status?.startsWith("verified") && <span className="inline-flex items-center gap-1 bg-[#2f7d4f] text-white px-2 py-0.5 text-[10px] font-bold uppercase"><BadgeCheck size={10} /> {VERIF[b.verification_status]}</span>}
                </div>
              </div>
              {l.images?.length > 0 && (
                <div className="bg-forest" data-testid="listing-gallery">
                  <img src={selVariant?.image || l.images[gi] || l.images[0]} alt={l.title} className="w-full max-h-[360px] object-cover" data-testid="listing-gallery-main" />
                  {l.images.length > 1 && (
                    <div className="flex gap-2 p-3 overflow-x-auto hr-scroll">
                      {l.images.map((im, n) => (
                        <button key={n} onClick={() => setGi(n)} className={`w-16 h-16 shrink-0 overflow-hidden border-2 transition-colors ${n === gi ? "border-blaze" : "border-transparent opacity-70 hover:opacity-100"}`} data-testid={`listing-thumb-${n}`}>
                          <img src={im} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="p-6 space-y-3">
                <div className="font-serif text-4xl text-forest tabular-nums">${effPrice?.toLocaleString()}</div>
                {l.description && <p className="text-charcoal/75 text-sm">{l.description}</p>}
                {variants.length > 0 && (
                  <div data-testid="modal-variants">
                    <span className="hr-overline text-saddle">Choose an option</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {variants.map((v) => {
                        const out = v.stock != null && v.stock <= 0;
                        return (
                          <button key={v.id} onClick={() => !out && setVariantId(v.id)} disabled={out} className={`px-3 py-1.5 text-sm font-semibold border transition-colors ${variantId === v.id ? "bg-forest text-bone border-forest" : out ? "border-saddle/20 text-charcoal/30 line-through cursor-not-allowed" : "border-saddle/30 text-forest hover:border-forest"}`} data-testid={`variant-opt-${v.id}`}>
                            {v.label}{v.price != null ? ` · $${v.price.toLocaleString()}` : ""}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 text-xs">
                  {l.species && <span className="bg-blaze/10 text-blaze-dark px-2 py-1 font-semibold">{l.species}</span>}
                  {l.state && <span className="bg-forest/10 text-forest px-2 py-1 font-semibold inline-flex items-center gap-1"><MapPin size={11} /> {l.state}</span>}
                  {l.spots_available != null && <span className="bg-saddle/15 text-saddle px-2 py-1 font-semibold">{l.spots_available} spots</span>}
                  {selStock != null && selStock > 0 && selStock <= 5 && <span className="bg-blaze/15 text-blaze-dark px-2 py-1 font-semibold" data-testid="modal-low-stock">Only {selStock} left</span>}
                </div>
                {l.listing_type === "product" && (l.shipping_cost != null || l.shipping_days) && (
                  <div className="flex items-center gap-2 text-sm text-charcoal/70 bg-bone/60 px-3 py-2" data-testid="modal-shipping">
                    <Truck size={14} className="text-saddle" />
                    <span>{l.shipping_cost > 0 ? `Shipping $${l.shipping_cost.toLocaleString()}` : "Free shipping"}{l.shipping_days ? ` · ${l.shipping_days}` : ""}</span>
                  </div>
                )}
                {l.shipping_cost > 0 && (
                  <div className="flex items-center justify-between text-sm border-t border-saddle/15 pt-2" data-testid="modal-total">
                    <span className="text-charcoal/60">Total</span>
                    <span className="font-serif text-2xl text-forest tabular-nums">${(Number(effPrice) + Number(l.shipping_cost)).toLocaleString()}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-xs text-charcoal/50 pt-2"><ShieldCheck size={13} className="text-forest" /> Secure checkout by Stripe · HomeRange buyer protection</div>
              </div>
              <div className="p-6 pt-0">
                {soldOut ? (
                  <button disabled className="w-full bg-charcoal/20 text-charcoal/50 py-3.5 font-semibold cursor-not-allowed" data-testid="listing-soldout-btn">Sold out</button>
                ) : (
                  <button onClick={buy} disabled={busy} className="w-full bg-forest text-bone py-3.5 font-semibold hover:bg-forest-2 transition-colors disabled:opacity-60" data-testid="listing-buy-btn">
                    {busy ? <Loader2 size={16} className="animate-spin inline" /> : (l.listing_type === "hunt" ? `Book this hunt · $${effPrice?.toLocaleString()}` : `Buy now · $${(Number(effPrice) + Number(l.shipping_cost || 0)).toLocaleString()}`)}
                  </button>
                )}
              </div>
              <ReviewsBlock listing={l} business={b} />
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function Marketplace() {
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();
  const [listings, setListings] = useState([]);
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [buyingId, setBuyingId] = useState(null);
  const [saved, setSaved] = useState([]);

  const load = useCallback(() => {
    api.marketplaceListings({ listing_type: type || undefined, category: category || undefined, q: search || undefined }).then(setListings).catch(() => {});
  }, [type, category, search]);
  useEffect(() => { const t = setTimeout(load, search ? 300 : 0); return () => clearTimeout(t); }, [load, search]);
  useEffect(() => { if (isAuthed) api.myWishlist().then((d) => setSaved(d.ids || [])).catch(() => {}); }, [isAuthed]);
  const [params] = useSearchParams();
  useEffect(() => { const lid = params.get("listing"); if (lid) setOpenId(lid); }, [params]);
  useEffect(() => { const c = params.get("category"); if (c) { setCategory(c); setType("product"); } }, [params]);
  const huntCtx = useMemo(() => {
    const species = params.get("species"); const weapon = params.get("weapon"); const title = params.get("hunt");
    return (species || weapon || title) ? { species, weapon, title } : null;
  }, [params]);
  const huntCats = useMemo(() => (huntCtx ? huntGearCategories(huntCtx) : []), [huntCtx]);

  const toggleSave = async (lid) => {
    if (!isAuthed) return openAuth("register");
    setSaved((p) => p.includes(lid) ? p.filter((x) => x !== lid) : [...p, lid]);
    try { await api.toggleWishlist(lid); } catch { api.myWishlist().then((d) => setSaved(d.ids || [])).catch(() => {}); }
  };

  const quickBuy = async (l) => {
    if (!isAuthed) return openAuth("register");
    if (l.variants?.length) return setOpenId(l.id);
    setBuyingId(l.id);
    try { const res = await api.marketplaceCheckout({ listing_id: l.id, origin_url: window.location.origin }); window.location.href = res.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBuyingId(null); }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="marketplace-page">
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/")} className="flex w-fit items-center gap-1.5 text-sm text-saddle hover:text-forest mb-6" data-testid="marketplace-back"><ArrowLeft size={15} /> Back to HomeRange</button>
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="hr-overline text-saddle">HomeRange Marketplace</span>
            <h1 className="font-serif text-4xl sm:text-5xl text-forest mt-2">Book hunts. Shop gear. Support outfitters.</h1>
            <p className="text-charcoal/60 mt-2 text-sm">Every listing comes from a subscribed, verifiable HomeRange business.</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => navigate("/wishlist")} className="inline-flex items-center gap-1.5 border border-saddle/30 text-forest px-4 py-2 text-sm font-semibold hover:border-forest transition-colors" data-testid="marketplace-saved-link"><Heart size={15} /> Saved</button>
            <button onClick={() => navigate("/orders")} className="inline-flex items-center gap-1.5 border border-saddle/30 text-forest px-4 py-2 text-sm font-semibold hover:border-forest transition-colors" data-testid="marketplace-orders-link"><Package size={15} /> My Orders</button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {TYPES.map((t) => <button key={t.k} onClick={() => { setType(t.k); if (t.k && t.k !== "product") setCategory(""); }} className={`px-4 py-2 text-sm font-semibold border transition-colors ${type === t.k ? "bg-forest text-bone border-forest" : "border-saddle/25 text-forest hover:border-forest"}`} data-testid={`market-type-${t.k || "all"}`}>{t.label}</button>)}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search listings, outfitters…" className="w-full border border-saddle/20 bg-white pl-9 pr-3 py-2.5 text-sm outline-none focus:border-forest" data-testid="market-search" />
          </div>
        </div>

        {huntCtx && huntCats.length > 0 && (
          <div className="mt-5 bg-forest text-bone p-4 sm:p-5" data-testid="hunt-gear-strip">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-blaze" />
              <span className="hr-overline text-blaze text-[10px]">Gear up for your hunt</span>
            </div>
            <p className="font-serif text-xl mt-1">{[huntCtx.title, huntCtx.species && `${huntCtx.species}`, huntCtx.weapon].filter(Boolean).join(" · ") || "Your hunt"}</p>
            <p className="text-bone/60 text-xs mt-0.5">Recommended categories for this hunt — tap to shop.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {huntCats.map((c) => (
                <button key={c.key} onClick={() => { setCategory(c.key); setType("product"); }}
                  className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${category === c.key ? "bg-blaze text-white border-blaze" : "border-bone/30 text-bone hover:border-blaze hover:text-blaze"}`}
                  data-testid={`hunt-gear-cat-${c.key.toLowerCase().replace(/[^a-z]+/g, "-")}`} title={c.reason}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {(type === "" || type === "product") && (
          <div className="mt-3 flex gap-2 overflow-x-auto hr-scroll pb-1" data-testid="market-category-bar">
            <button onClick={() => setCategory("")} className={`shrink-0 px-3 py-1.5 text-xs font-semibold border transition-colors ${category === "" ? "bg-blaze text-white border-blaze" : "border-saddle/25 text-charcoal/60 hover:border-blaze"}`} data-testid="market-cat-all">All gear</button>
            {GEAR_CATS.map((c) => <button key={c} onClick={() => { setCategory(c); if (type === "") setType("product"); }} className={`shrink-0 px-3 py-1.5 text-xs font-semibold border transition-colors ${category === c ? "bg-blaze text-white border-blaze" : "border-saddle/25 text-charcoal/60 hover:border-blaze"}`} data-testid={`market-cat-${c}`}>{c}</button>)}
          </div>
        )}

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="market-grid">
          {listings.map((l) => (
            <motion.div key={l.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} onClick={() => setOpenId(l.id)} style={l.accent ? { borderTopColor: l.accent, borderTopWidth: 3 } : {}} className="bg-white border border-saddle/20 hover:border-forest/40 hover:-translate-y-0.5 transition-[transform,border-color] text-left flex flex-col cursor-pointer overflow-hidden" data-testid="market-card">
              <div className="h-48 bg-bone/60 overflow-hidden relative">
                {l.images?.[0]
                  ? <img src={l.images[0]} alt={l.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" data-testid="market-card-image" />
                  : <div className="w-full h-full grid place-items-center text-saddle/40"><Store size={28} /></div>}
                {l.images?.length > 1 && <span className="absolute bottom-2 right-2 bg-forest/85 text-bone text-[10px] font-semibold px-2 py-0.5">{l.images.length} photos</span>}
                {l.sponsored && <span className="absolute top-2 left-2 text-[9px] font-bold uppercase bg-blaze text-white px-1.5 py-0.5" data-testid="sponsored-label">Sponsored</span>}
                {l.listing_type === "product" && <button onClick={(e) => { e.stopPropagation(); toggleSave(l.id); }} className="absolute top-2 right-2 bg-white/85 hover:bg-white rounded-full p-1.5 transition-colors" aria-label="Save for later" data-testid="card-save-btn"><Heart size={15} className={saved.includes(l.id) ? "fill-blaze text-blaze" : "text-charcoal/50"} /></button>}
                {effStock(l) != null && effStock(l) <= 0 && <span className="absolute bottom-2 left-2 text-[9px] font-bold uppercase bg-charcoal text-white px-1.5 py-0.5" data-testid="sold-out-badge">Sold out</span>}
                {effStock(l) != null && effStock(l) > 0 && effStock(l) <= 5 && <span className="absolute bottom-2 left-2 text-[9px] font-bold uppercase bg-blaze text-white px-1.5 py-0.5" data-testid="low-stock-badge">Only {effStock(l)} left</span>}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-[10px] font-bold uppercase bg-saddle/15 text-saddle px-2 py-0.5">{l.listing_type}</span>
                  {l.variants?.length > 0 && <span className="text-[10px] font-semibold text-saddle/70">{l.variants.length} options</span>}
                  {l.verification_status?.startsWith("verified") && <span className="inline-flex items-center gap-1 text-[9px] font-bold text-[#2f7d4f] uppercase" data-testid="card-verified-seal" title={VERIF[l.verification_status]}><img src="/brand/mark_forest.png" alt="Verified" className="w-4 h-4 object-contain" /> Verified</span>}
                </div>
                <h3 className="font-serif text-lg text-forest leading-tight">{l.title}</h3>
                <button onClick={(e) => { e.stopPropagation(); navigate(`/store/${l.business_id}`); }} className="text-xs text-saddle hover:text-blaze mt-0.5 text-left" data-testid="market-storefront-link">{l.business_name} →</button>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {l.species && <span className="bg-blaze/10 text-blaze-dark px-2 py-0.5 text-[11px] font-semibold">{l.species}</span>}
                  {l.state && <span className="bg-forest/10 text-forest px-2 py-0.5 text-[11px] font-semibold">{l.state}</span>}
                  {l.shipping_cost != null && l.listing_type === "product" && <span className="bg-saddle/10 text-saddle px-2 py-0.5 text-[11px] font-semibold inline-flex items-center gap-1"><Truck size={11} /> {l.shipping_cost > 0 ? `+$${l.shipping_cost} ship` : "Free ship"}</span>}
                </div>
                <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                  <span className="font-serif text-2xl tabular-nums" style={{ color: l.accent || "#1F2A22" }}>${l.price?.toLocaleString()}</span>
                  {effStock(l) != null && effStock(l) <= 0 ? (
                    <span className="text-xs font-semibold text-charcoal/40" data-testid="card-soldout">Sold out</span>
                  ) : (
                    <button onClick={(e) => { e.stopPropagation(); quickBuy(l); }} disabled={buyingId === l.id} className="bg-forest text-bone px-4 py-2 text-sm font-semibold hover:bg-forest-2 transition-colors disabled:opacity-60" data-testid="card-buy-btn">
                      {buyingId === l.id ? <Loader2 size={14} className="animate-spin" /> : (l.variants?.length ? "Options" : l.listing_type === "hunt" ? "Book" : "Buy")}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
          {listings.length === 0 && <p className="text-charcoal/50 col-span-full text-center py-10" data-testid="market-empty">No listings yet. Check back soon — or list your business.</p>}
        </div>
      </div>
      <ListingModal id={openId} onClose={() => setOpenId(null)} saved={saved.includes(openId)} onToggleSave={toggleSave} />
    </div>
  );
}
